import {
  Datagrid,
  List,
  Create,
  Edit,
  SimpleForm,
  TextField,
  TextInput,
  BooleanField,
  Show,
  SimpleShowLayout,
  usePermissions,
  useRecordContext,
  ReferenceInput,
  AutocompleteInput,
  required,
} from 'react-admin';
import { Chip } from '@mui/material';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import { ArchivedSelect } from '../components/selects';
import { hasRole, type Permissions } from '../types';

// --- Taxonomy (top-level taxonomy definitions) ---

export const TaxonomyList = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <List
      exporter={false}
      filters={[<ArchivedSelect key="archived" />]}
      sort={{ field: 'label', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}>
        <TextField source="taxonomyKey" label="Key" />
        <TextField source="label" label="Label" />
        <TextField source="description" label="Description" />
        <ArchivedChipField />
      </Datagrid>
    </List>
  );
};

const ArchivedChipField = () => {
  const record = useRecordContext();
  if (!record) return null;
  return record.archived_at ? (
    <Chip label="Archived" size="small" color="warning" />
  ) : (
    <Chip label="Active" size="small" color="success" />
  );
};
ArchivedChipField.displayName = 'ArchivedChipField';

export const TaxonomyShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="taxonomyKey" label="Key" />
      <TextField source="label" label="Label" />
      <TextField source="description" label="Description" />
      <BooleanField source="archived_at" label="Archived" />
    </SimpleShowLayout>
  </Show>
);

export const TaxonomyCreate = () => (
  <Create>
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <TextInput fullWidth source="taxonomyKey" label="Key" />
      <TextInput fullWidth source="label" label="Label" />
      <TextInput fullWidth multiline source="description" label="Description" />
    </SimpleForm>
  </Create>
);

export const TaxonomyEdit = () => (
  <Edit mutationMode="pessimistic">
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <TextInput fullWidth source="taxonomyKey" label="Key" disabled />
      <TextInput fullWidth source="label" label="Label" />
      <TextInput fullWidth multiline source="description" label="Description" />
    </SimpleForm>
  </Edit>
);

// --- Taxonomy Terms (values within a taxonomy, e.g. specific AFSCs or MAJCOMs) ---

export const TaxonomyTermList = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <List
      exporter={false}
      filters={[<ArchivedSelect key="archived" />]}
      sort={{ field: 'label', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}>
        <TextField source="code" label="Code" />
        <TextField source="label" label="Label" />
        <TextField source="parentCode" label="Parent Code" />
        <ArchivedChipField />
      </Datagrid>
    </List>
  );
};

export const TaxonomyTermShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="code" label="Code" />
      <TextField source="label" label="Label" />
      <TextField source="parentCode" label="Parent Code" />
      <BooleanField source="archived_at" label="Archived" />
    </SimpleShowLayout>
  </Show>
);

export const TaxonomyTermCreate = () => (
  <Create>
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <ReferenceInput source="taxonomyKey" reference="taxonomies"><AutocompleteInput optionText="label" validate={required()} /></ReferenceInput>
      <TextInput fullWidth source="code" label="Code" validate={required()} />
      <TextInput fullWidth source="label" label="Label" />
      <TextInput fullWidth source="parentCode" label="Parent Code" />
    </SimpleForm>
  </Create>
);

export const TaxonomyTermEdit = () => (
  <Edit mutationMode="pessimistic">
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <TextInput fullWidth source="taxonomyKey" label="Taxonomy" disabled />
      <TextInput fullWidth source="code" label="Code" disabled />
      <TextInput fullWidth source="label" label="Label" />
      <TextInput fullWidth source="parentCode" label="Parent Code" />
    </SimpleForm>
  </Edit>
);

export const taxonomies = {
  list: TaxonomyList,
  show: TaxonomyShow,
  create: TaxonomyCreate,
  edit: TaxonomyEdit,
};

export const taxonomy_terms = {
  list: TaxonomyTermList,
  show: TaxonomyTermShow,
  create: TaxonomyTermCreate,
  edit: TaxonomyTermEdit,
};
